import React, { useRef } from "react";
import { cn } from "../../../lib/utils";
import { FOCUS_RING } from "./classes";

/**
 * The client-detail tab row: the active tab is a black pill (wireframe), the rest plain
 * text. ARIA tabs pattern with roving focus (← → Home End). Panels are rendered by the
 * caller with `id={panelId(key)}` and `aria-labelledby={tabId(key)}`.
 *
 * `tabs` is `[{key, label, icon?, testId?}]`.
 */
export const tabId = (key) => `crm-tab-${key}`;
export const panelId = (key) => `crm-tabpanel-${key}`;

export default function CrmTabs({ tabs, value, onChange, label, className }) {
  const refs = useRef({});
  const move = (index) => {
    const next = tabs[(index + tabs.length) % tabs.length];
    onChange(next.key);
    refs.current[next.key]?.focus();
  };
  const onKeyDown = (e, i) => {
    if (e.key === "ArrowRight") { e.preventDefault(); move(i + 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); move(i - 1); }
    else if (e.key === "Home") { e.preventDefault(); move(0); }
    else if (e.key === "End") { e.preventDefault(); move(tabs.length - 1); }
  };
  return (
    <div role="tablist" aria-label={label} className={cn("flex flex-wrap gap-x-1 gap-y-2", className)}>
      {tabs.map((tab, i) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(el) => { refs.current[tab.key] = el; }}
            type="button"
            role="tab"
            id={tabId(tab.key)}
            aria-selected={active}
            aria-controls={panelId(tab.key)}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
            data-testid={tab.testId}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors",
              active ? "bg-eh-ink text-eh-bg" : "text-eh-ink hover:bg-eh-cream-deep",
              FOCUS_RING,
            )}
          >
            {tab.label}
            {tab.icon}
          </button>
        );
      })}
    </div>
  );
}
